import React, { useState, useEffect, useCallback } from 'react';
import { getAccessToken } from '@/lib/tokenStore';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Loader2, ShoppingCart, Search, Plus, Minus, Send, Save, Package, Clock,
  CheckCircle2, ChevronRight, ChevronDown, Trash2, Mail, Download, ArrowLeft, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdrs = () => {
  const t = getAccessToken();
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('nl-NL', { day: '2-digit', month: 'long', year: 'numeric' }) : '-';
const fmtCur = (v) => `€${Number(v || 0).toFixed(2)}`;

const Bestellingen = () => {
  const { selectedLocatie, selectedLocatieData } = useOutletContext();
  const { user, isAdmin } = useAuth();
  const [tab, setTab] = useState('nieuw');
  const [leveranciers, setLeveranciers] = useState([]);
  const [bestellingen, setBestellingen] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBestellingen = useCallback(async () => {
    if (!selectedLocatie) return;
    try {
      const res = await fetch(`${API}/locaties/${selectedLocatie}/bestellingen?limit=100`, { headers: hdrs() });
      if (res.ok) setBestellingen(await res.json());
    } catch {}
  }, [selectedLocatie]);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/leveranciers-centraal`, { headers: hdrs() }).then(r => r.ok ? r.json() : []),
      selectedLocatie ? fetch(`${API}/locaties/${selectedLocatie}/bestellingen?limit=100`, { headers: hdrs() }).then(r => r.ok ? r.json() : []) : Promise.resolve([]),
    ]).then(([levs, best]) => {
      setLeveranciers(levs);
      setBestellingen(best);
    }).finally(() => setLoading(false));
  }, [selectedLocatie]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#1f2548]" /></div>;

  return (
    <div className="space-y-5 max-w-5xl mx-auto px-4 sm:px-0" data-testid="bestellingen-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-[#1f2548] flex items-center gap-2">
          <ShoppingCart className="w-6 h-6" /> Bestellingen
        </h1>
        <p className="text-sm text-slate-500">{selectedLocatieData?.naam}</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="bestelling-tabs">
        <button onClick={() => setTab('nieuw')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${tab === 'nieuw' ? 'bg-white text-[#1f2548] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          <Plus className="w-4 h-4 inline mr-1" /> Nieuwe bestelling
        </button>
        <button onClick={() => { setTab('mijn'); fetchBestellingen(); }}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${tab === 'mijn' ? 'bg-white text-[#1f2548] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          <Package className="w-4 h-4 inline mr-1" /> Mijn bestellingen
          {bestellingen.length > 0 && <Badge className="ml-1 bg-blue-100 text-blue-700 text-xs">{bestellingen.length}</Badge>}
        </button>
      </div>

      {tab === 'nieuw' && (
        <NieuweBestelling
          leveranciers={leveranciers}
          locatieId={selectedLocatie}
          locatieNaam={selectedLocatieData?.naam}
          user={user}
          onCreated={() => { setTab('mijn'); fetchBestellingen(); }}
        />
      )}

      {tab === 'mijn' && (
        <MijnBestellingen
          bestellingen={bestellingen}
          locatieId={selectedLocatie}
          onRefresh={fetchBestellingen}
          isAdmin={isAdmin()}
        />
      )}
    </div>
  );
};

// ── Nieuwe Bestelling Tab ────────────────────────────────────────────────────
const NieuweBestelling = ({ leveranciers, locatieId, locatieNaam, user, onCreated }) => {
  const [selectedLev, setSelectedLev] = useState('');
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState({});
  const [stockLevels, setStockLevels] = useState({});
  const [notities, setNotities] = useState('');
  const [loadingProds, setLoadingProds] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!selectedLev) { setProducts([]); setCart({}); setStockLevels({}); return; }
    setLoadingProds(true);
    fetch(`${API}/producten-catalogus?leverancier_id=${selectedLev}`, { headers: hdrs() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setProducts(d))
      .finally(() => setLoadingProds(false));
  }, [selectedLev]);

  const filtered = products.filter(p => !search || p.naam.toLowerCase().includes(search.toLowerCase()));
  const levObj = leveranciers.find(l => l._id === selectedLev);

  const updateCart = (pid, qty) => setCart(prev => {
    const c = { ...prev };
    if (qty <= 0) { delete c[pid]; setStockLevels(s => { const ns = {...s}; delete ns[pid]; return ns; }); }
    else c[pid] = qty;
    return c;
  });

  const updateStock = (pid, val) => setStockLevels(prev => ({ ...prev, [pid]: val }));

  const cartItems = Object.entries(cart).map(([pid, qty]) => {
    const p = products.find(x => x._id === pid);
    if (!p) return null;
    const vpe = p.verpakkingseenheid || 1;
    const sub = (p.prijs_excl_btw || 0) * qty;
    const btw = sub * 0.21;
    const totaalStuks = qty * vpe;
    const eenheidTekst = vpe > 1 ? `${qty}x ${vpe} stuks` : (p.beschrijving || 'per stuk');
    const huidigeVoorraad = stockLevels[pid];
    return { ...p, aantal: qty, verpakkingseenheid: vpe, totaal_stuks: totaalStuks, eenheid_tekst: eenheidTekst, huidige_voorraad: huidigeVoorraad, subtotaal: sub, btw_bedrag: btw, totaal_incl_btw: sub + btw };
  }).filter(Boolean);

  const totals = {
    sub: cartItems.reduce((s, i) => s + i.subtotaal, 0),
    btw: cartItems.reduce((s, i) => s + i.btw_bedrag, 0),
    tot: cartItems.reduce((s, i) => s + i.totaal_incl_btw, 0),
  };

  const handleSubmit = async (status) => {
    if (cartItems.length === 0) { toast.error('Selecteer minimaal 1 product'); return; }
    const missingStock = cartItems.some(ci => ci.huidige_voorraad === undefined || ci.huidige_voorraad === null || ci.huidige_voorraad === '');
    if (missingStock) { toast.error('Vul bij elk product de huidige voorraad in'); return; }
    setSubmitting(true);
    try {
      const body = {
        leverancierId: selectedLev,
        leverancierNaam: levObj?.bedrijfsnaam || '',
        notities,
        items: cartItems.map(ci => ({
          naam: ci.naam, aantal: ci.aantal,
          eenheid: ci.verpakkingseenheid > 1 ? `${ci.totaal_stuks} stuks (${ci.aantal}x ${ci.verpakkingseenheid})` : (ci.beschrijving || 'per stuk'),
          prijs_excl_btw: ci.prijs_excl_btw, btw_percentage: 21,
          subtotaal: ci.subtotaal, btw_bedrag: ci.btw_bedrag, totaal_incl_btw: ci.totaal_incl_btw,
          foto_url: ci.foto_url || '', extern_id: ci.extern_id,
          verpakkingseenheid: ci.verpakkingseenheid || 1,
          huidige_voorraad: Number(ci.huidige_voorraad) || 0,
          product_id: ci._id,
        })),
      };
      const res = await fetch(`${API}/locaties/${locatieId}/bestellingen`, { method: 'POST', headers: hdrs(), body: JSON.stringify(body) });
      if (!res.ok) { toast.error('Bestelling aanmaken mislukt'); return; }
      const data = await res.json();
      const bId = data.bestelling?._id;

      if (status === 'verzonden' && bId) {
        // 4.4a: de echte uitkomst tonen, niet blind "verzonden + email"
        const vz = await fetch(`${API}/locaties/${locatieId}/bestellingen/${bId}/verzend`,
          { method: 'PATCH', headers: hdrs() });
        const vd = await vz.json().catch(() => ({}));
        if (!vz.ok) toast.error(vd.detail || 'Verzenden mislukt — bestelling staat op concept');
        else if (vd.email_verstuurd) toast.success(vd.message || 'Bestelling verzonden');
        else toast.warning(vd.message || 'Bestelling verzonden, maar de e-mail is niet verstuurd');
      } else {
        toast.success('Bestelling opgeslagen als concept');
      }
      onCreated();
    } catch { toast.error('Fout bij aanmaken'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4">
      {/* Leverancier select */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <Label className="text-sm font-semibold text-[#1f2548]">1. Kies leverancier</Label>
        <Select value={selectedLev} onValueChange={v => { setSelectedLev(v); setCart({}); setSearch(''); }} data-testid="bestelling-lev-select">
          <SelectTrigger className="mt-2" data-testid="bestelling-leverancier-trigger"><SelectValue placeholder="Selecteer leverancier..." /></SelectTrigger>
          <SelectContent>
            {leveranciers.map(l => <SelectItem key={l._id} value={l._id}>{l.bedrijfsnaam}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {selectedLev && (
        <>
          {/* Products */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <Label className="text-sm font-semibold text-[#1f2548]">2. Producten selecteren</Label>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Zoek product..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="bestelling-search" />
            </div>
            {loadingProds ? (
              <div className="text-center py-8 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-center py-6 text-slate-400">{products.length === 0 ? 'Geen catalogus voor deze leverancier' : 'Geen producten gevonden'}</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-3 max-h-[400px] overflow-y-auto" data-testid="product-grid">
                {filtered.map(p => {
                  const qty = cart[p._id] || 0;
                  const vpe = p.verpakkingseenheid || 1;
                  return (
                    <div key={p._id} className={`rounded-lg border p-2.5 transition-all ${qty > 0 ? 'border-[#1f2548] bg-blue-50/40 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}>
                      {p.foto_url && <img src={p.foto_url} alt="" className="w-full h-24 object-contain rounded bg-white mb-1.5" loading="lazy" />}
                      <p className="text-xs font-medium text-slate-800 truncate" title={p.naam}>{p.naam}</p>
                      <p className="text-[10px] text-slate-400">{p.beschrijving}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <p className="text-sm font-bold text-[#1f2548]">{fmtCur(p.prijs_excl_btw)}</p>
                        {vpe > 1 && <span className="text-[10px] text-slate-400">/ {vpe} st</span>}
                      </div>
                      <div className="flex items-center gap-1 mt-1.5">
                        <button onClick={() => updateCart(p._id, Math.max(0, qty - 1))} className="w-7 h-7 rounded bg-slate-200 text-slate-700 text-sm font-bold hover:bg-slate-300 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                        <Input type="number" min="0" value={qty || ''} onChange={e => updateCart(p._id, parseInt(e.target.value) || 0)} className="h-7 w-14 text-center text-xs p-0" />
                        <button onClick={() => updateCart(p._id, qty + 1)} className="w-7 h-7 rounded bg-[#1f2548] text-white text-sm font-bold hover:bg-[#2a3060] flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                      </div>
                      {qty > 0 && vpe > 1 && <p className="text-[10px] text-blue-600 mt-0.5">{qty}x {vpe} = {qty * vpe} stuks</p>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Cart / Order summary */}
          {cartItems.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3" data-testid="bestelling-cart">
              <Label className="text-sm font-semibold text-[#1f2548]">3. Bestelmandje ({cartItems.length} producten)</Label>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-slate-500">
                      <th className="py-2 pr-2">Product</th>
                      <th className="py-2 px-2 text-center">Besteld</th>
                      <th className="py-2 px-2 text-center">Voorraad nu</th>
                      <th className="py-2 px-2 text-right">Stukprijs</th>
                      <th className="py-2 px-2 text-right">Subtotaal</th>
                      <th className="py-2 px-2 text-right">BTW</th>
                      <th className="py-2 pl-2 text-right">Totaal</th>
                      <th className="py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cartItems.map(ci => (
                      <tr key={ci._id} className="border-b border-slate-50">
                        <td className="py-2 pr-2">
                          <div className="flex items-center gap-2">
                            {ci.foto_url && <img src={ci.foto_url} alt="" className="w-8 h-8 rounded object-cover" />}
                            <span className="truncate max-w-[150px]">{ci.naam}</span>
                          </div>
                        </td>
                        <td className="py-2 px-2 text-center font-medium">
                          {ci.verpakkingseenheid > 1 ? <span className="text-xs">{ci.eenheid_tekst}</span> : ci.aantal}
                        </td>
                        <td className="py-2 px-2 text-center">
                          <Input type="number" min="0" value={stockLevels[ci._id] ?? ''} onChange={e => updateStock(ci._id, e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            className="h-7 w-16 text-center text-xs p-0 mx-auto" placeholder="0" data-testid={`stock-${ci._id}`} />
                        </td>
                        <td className="py-2 px-2 text-right text-slate-600">{fmtCur(ci.prijs_excl_btw)}</td>
                        <td className="py-2 px-2 text-right">{fmtCur(ci.subtotaal)}</td>
                        <td className="py-2 px-2 text-right text-slate-400">{fmtCur(ci.btw_bedrag)}</td>
                        <td className="py-2 pl-2 text-right font-bold text-[#1f2548]">{fmtCur(ci.totaal_incl_btw)}</td>
                        <td className="py-2"><button onClick={() => updateCart(ci._id, 0)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end">
                <div className="bg-slate-50 rounded-lg border p-3 space-y-1 text-sm min-w-[220px]">
                  <div className="flex justify-between"><span className="text-slate-500">Subtotaal excl. BTW:</span><span>{fmtCur(totals.sub)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">BTW (21%):</span><span>{fmtCur(totals.btw)}</span></div>
                  <div className="flex justify-between font-bold text-[#1f2548] border-t pt-1 mt-1"><span>Totaal incl. BTW:</span><span>{fmtCur(totals.tot)}</span></div>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Notities (optioneel)</Label>
                <Textarea value={notities} onChange={e => setNotities(e.target.value)} placeholder="Extra opmerkingen voor de leverancier..." rows={2} className="mt-1" />
              </div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pt-2 border-t gap-3">
                <p className="text-xs text-slate-400">Afleverlocatie: {locatieNaam}</p>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <Button variant="outline" onClick={() => handleSubmit('concept')} disabled={submitting} data-testid="save-concept-btn" className="w-full sm:w-auto">
                    <Save className="w-4 h-4 mr-1" /> Opslaan als concept
                  </Button>
                  <Button onClick={() => handleSubmit('verzonden')} disabled={submitting} className="bg-[#1f2548] hover:bg-[#2a3060] w-full sm:w-auto" data-testid="verzend-bestelling-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                    Verzenden
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── Mijn Bestellingen Tab ────────────────────────────────────────────────────
const MijnBestellingen = ({ bestellingen, locatieId, onRefresh, isAdmin }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [probleemModal, setProbleemModal] = useState(null); // bestelling object

  const statusConfig = (s) => {
    if (s === 'concept') return { label: 'Concept', cls: 'bg-slate-100 text-slate-700', icon: Clock };
    if (s === 'verzonden') return { label: 'Verzonden', cls: 'bg-blue-100 text-blue-700', icon: Send };
    if (s === 'ontvangen') return { label: 'Ontvangen', cls: 'bg-green-100 text-green-700', icon: CheckCircle2 };
    if (s === 'deels_ontvangen') return { label: 'Deels ontvangen', cls: 'bg-amber-100 text-amber-700', icon: Package };
    if (s === 'probleem_gemeld') return { label: 'Probleem gemeld', cls: 'bg-red-100 text-red-700', icon: Package };
    return { label: s, cls: 'bg-slate-100 text-slate-600', icon: Package };
  };

  const handleVerzend = async (bId) => {
    setActionLoading(bId);
    try {
      const res = await fetch(`${API}/locaties/${locatieId}/bestellingen/${bId}/verzend`, { method: 'PATCH', headers: hdrs() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(d.detail || 'Verzenden mislukt — bestelling staat nog op concept');
      } else if (d.email_verstuurd) {
        toast.success(d.message || 'Bestelling verzonden');
        onRefresh();
      } else {
        toast.warning(d.message || 'Bestelling verzonden, maar de e-mail is niet verstuurd');
        onRefresh();
      }
    } catch { toast.error('Fout'); } finally { setActionLoading(null); }
  };

  const handleResendEmail = async (bId) => {
    setActionLoading(bId);
    try {
      const res = await fetch(`${API}/locaties/${locatieId}/bestellingen/${bId}/verzend-email`, { method: 'POST', headers: hdrs() });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { toast.success(d.message || 'E-mail opnieuw verstuurd'); onRefresh(); }
      else toast.error(d.detail || 'E-mail verzenden mislukt');
    } catch { toast.error('Fout'); } finally { setActionLoading(null); }
  };

  const handleOntvangen = async (bId) => {
    setActionLoading(bId);
    try {
      const res = await fetch(`${API}/locaties/${locatieId}/bestellingen/${bId}/status`, {
        method: 'PATCH', headers: hdrs(), body: JSON.stringify({ status: 'ontvangen' }),
      });
      if (!res.ok) { toast.error('Ontvangen markeren mislukt'); return; }
      toast.success('Ontvangen — voorraad bijgewerkt');
      onRefresh();
    } catch { toast.error('Fout'); } finally { setActionLoading(null); }
  };

  const handleDelete = async (bId) => {
    if (!confirm('Bestelling verwijderen?')) return;
    try {
      const res = await fetch(`${API}/locaties/${locatieId}/bestellingen/${bId}`,
        { method: 'DELETE', headers: hdrs() });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Verwijderen mag alleen door een beheerder');
        return;
      }
      toast.success('Bestelling verwijderd'); onRefresh();
    } catch { toast.error('Fout'); }
  };

  if (bestellingen.length === 0) return (
    <div className="bg-white rounded-xl border p-10 text-center"><Package className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-400">Nog geen bestellingen</p></div>
  );

  return (
    <div className="space-y-3">
      {bestellingen.map(b => {
        const sc = statusConfig(b.status);
        const Icon = sc.icon;
        const isExpanded = expandedId === b._id;
        const items = b.items || [];
        const totaal = items.reduce((s, i) => s + (i.totaal_incl_btw || 0), 0);
        return (
          <div key={b._id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50" onClick={() => setExpandedId(isExpanded ? null : b._id)}>
              <div className="flex items-center gap-3">
                {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                <div>
                  <p className="font-medium text-slate-800">{b.leverancierNaam || 'Onbekend'}</p>
                  <p className="text-xs text-slate-500">{fmtDate(b.aangemaakt)} — {items.length} producten</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {totaal > 0 && <span className="font-bold text-[#1f2548]">{fmtCur(totaal)}</span>}
                <Badge className={sc.cls}><Icon className="w-3 h-3 mr-1" />{sc.label}</Badge>
              </div>
            </div>
            {isExpanded && (
              <div className="border-t bg-slate-50 px-4 py-4 space-y-3">
                {/* Items */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-slate-500 border-b">
                      <th className="py-1.5">Product</th><th className="text-center">Aantal</th><th className="text-right">Stukprijs</th><th className="text-right">Subtotaal</th><th className="text-right">Totaal</th>
                    </tr></thead>
                    <tbody>
                      {items.map((it, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-1.5"><div className="flex items-center gap-2">{it.foto_url && <img src={it.foto_url} alt="" className="w-7 h-7 rounded object-cover" />}<span className="truncate max-w-[200px]">{it.naam}</span></div></td>
                          <td className="text-center">{it.aantal}</td>
                          <td className="text-right text-slate-500">{fmtCur(it.prijs_excl_btw)}</td>
                          <td className="text-right">{fmtCur(it.subtotaal)}</td>
                          <td className="text-right font-medium">{fmtCur(it.totaal_incl_btw)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {b.notities && <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-sm text-amber-700"><b>Notities:</b> {b.notities}</div>}
                {b.laatste_email_verzonden && <p className="text-xs text-slate-400">Email verstuurd op {fmtDate(b.laatste_email_verzonden)}</p>}
                <div className="flex gap-2 flex-wrap pt-2 border-t">
                  {b.status === 'concept' && (
                    <>
                      <Button size="sm" onClick={() => handleVerzend(b._id)} disabled={actionLoading === b._id} className="bg-[#1f2548] hover:bg-[#2a3060]">
                        {actionLoading === b._id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />} Verzenden
                      </Button>
                      {isAdmin && (
                        <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDelete(b._id)} data-testid={`concept-delete-${b._id}`}><Trash2 className="w-3.5 h-3.5 mr-1" /> Verwijderen</Button>
                      )}
                    </>
                  )}
                  {b.status === 'verzonden' && (
                    <>
                      <Button size="sm" onClick={() => handleOntvangen(b._id)} disabled={actionLoading === b._id} className="bg-green-600 hover:bg-green-700">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Ontvangen markeren
                      </Button>
                      <Button size="sm" variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50"
                        onClick={() => setProbleemModal(b)} data-testid="probleem-melden-btn">
                        <Package className="w-3.5 h-3.5 mr-1" /> Probleem melden
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleResendEmail(b._id)} disabled={actionLoading === b._id} data-testid="resend-email-btn">
                        <Mail className="w-3.5 h-3.5 mr-1" /> Email opnieuw versturen
                      </Button>
                    </>
                  )}
                  {(b.status === 'ontvangen' || b.status === 'deels_ontvangen' || b.status === 'probleem_gemeld') && (
                    <p className="text-sm text-green-600 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Afgerond</p>
                  )}
                  {isAdmin && b.status !== 'concept' && (
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 hover:bg-red-50 ml-auto"
                      onClick={() => handleDelete(b._id)} data-testid={`admin-delete-${b._id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Verwijderen
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Probleem Modal */}
      {probleemModal && (
        <ProbleemModal bestelling={probleemModal} locatieId={locatieId}
          onClose={() => setProbleemModal(null)} onSaved={() => { setProbleemModal(null); onRefresh(); }} />
      )}
    </div>
  );
};

// ── Probleem Melden Modal ────────────────────────────────────────────────────
const ProbleemModal = ({ bestelling, locatieId, onClose, onSaved }) => {
  const items = bestelling.items || [];
  const [rows, setRows] = useState(items.map(it => ({
    naam: it.naam, besteld: it.aantal, ontvangen: it.aantal, probleem: 'geen', foto_url: it.foto_url, product_id: it.product_id, huidige_voorraad: it.huidige_voorraad || 0,
  })));
  const [toelichting, setToelichting] = useState('');
  const [saving, setSaving] = useState(false);

  const updateRow = (i, field, val) => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  const hasProbleem = rows.some(r => r.probleem !== 'geen');

  const handleSubmit = async () => {
    if (hasProbleem && !toelichting.trim()) { toast.error('Vul een toelichting in bij een probleem'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/locaties/${locatieId}/bestellingen/${bestelling._id}/probleem`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({ items: rows, toelichting }),
      });
      if (res.ok) { toast.success('Probleem gemeld'); onSaved(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Fout bij melden'); }
    } catch { toast.error('Fout'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="probleem-modal">
        <DialogHeader>
          <DialogTitle>Probleem melden — {bestelling.leverancierNaam}</DialogTitle>
          <DialogDescription>Geef per product aan wat er ontvangen is.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {rows.map((row, i) => (
            <div key={i} className="bg-slate-50 rounded-lg border p-3 space-y-2">
              <div className="flex items-center gap-2">
                {row.foto_url && <img src={row.foto_url} alt="" className="w-8 h-8 rounded object-cover" />}
                <span className="text-sm font-medium text-slate-800 flex-1">{row.naam}</span>
                <span className="text-xs text-slate-400">Besteld: {row.besteld}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Werkelijk ontvangen</Label>
                  <Input type="number" min="0" value={row.ontvangen}
                    onChange={e => updateRow(i, 'ontvangen', parseInt(e.target.value) || 0)}
                    className="h-8 text-sm" data-testid={`ontvangen-${i}`} />
                </div>
                <div>
                  <Label className="text-xs">Probleem</Label>
                  <Select value={row.probleem} onValueChange={v => updateRow(i, 'probleem', v)}>
                    <SelectTrigger className="h-8 text-sm" data-testid={`probleem-type-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="geen">Geen</SelectItem>
                      <SelectItem value="onvolledig">Onvolledig</SelectItem>
                      <SelectItem value="beschadigd">Beschadigd</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
          <div>
            <Label className="text-sm">Toelichting {hasProbleem && <span className="text-red-500">*</span>}</Label>
            <Textarea value={toelichting} onChange={e => setToelichting(e.target.value)} rows={3}
              placeholder="Beschrijf het probleem..." className="mt-1" data-testid="probleem-toelichting" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="probleem-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Package className="w-4 h-4 mr-1" />}
            Probleem melden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Bestellingen;
